<template>
  <div class="tooltip-wrapper" :class="{ 'tw-relative': useRelativePositioning }">
    <div
      ref="tooltipTrigger"
      class="tooltip-trigger"
      :class="[triggerClass ? triggerClass : '']"
      @mouseover="handleMouseOver"
    >
      <slot name="trigger"></slot>
    </div>
    <span
      v-if="$slots.content"
      ref="tooltip"
      class="tooltip"
      :class="[
        'tw-bg-primary',
        'tw-text-primary-foreground',
        'tw-z-50',
        'tw-rounded-md',
        'tw-px-3',
        'tw-py-1.5',
        'tw-text-xs',
        tooltipClasses ? tooltipClasses : ''
      ]"
      :style="tooltipStyle"
    >
      <slot name="content"></slot>
    </span>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

interface Props {
  position: 'bottom' | 'top' | 'left' | 'right';
}

export default defineComponent({
  name: 'TooltipPopup',
  props: {
    tooltipClasses: {
      type: String,
      default: ''
    },
    triggerClass: {
      type: String,
      default: ''
    },
    position: {
      type: String as PropType<Props['position']>,
      default: 'top',
      validator: (prop: Props['position']) => ['bottom', 'top', 'left', 'right'].includes(prop)
    },
    useRelativePositioning: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tooltipStyle: {
        top: '0',
        left: '0'
      }
    };
  },
  methods: {
    handleMouseOver(): void {
      const tooltip: HTMLElement | null = this.$refs.tooltip as HTMLElement;
      if (!tooltip) {
        return;
      }

      const triggerElement: HTMLElement | null = this.$refs.tooltipTrigger as HTMLElement;
      if (!triggerElement) {
        return;
      }

      const triggerRect = triggerElement.getBoundingClientRect();

      const relativeTop = this.useRelativePositioning ? 0 : triggerRect.top;
      const relativeLeft = this.useRelativePositioning ? 0 : triggerRect.left;

      const tooltipHeight: number = tooltip.offsetHeight;
      const tooltipWidth: number = tooltip.offsetWidth;

      switch (this.position) {
        case 'top':
          this.tooltipStyle = {
            top: `${relativeTop - tooltipHeight}px`,
            left: `${relativeLeft - (tooltipWidth / 2 - triggerElement.offsetWidth / 2)}px`
          };
          break;
        case 'bottom':
          this.tooltipStyle = {
            top: `${relativeTop + triggerElement.offsetHeight}px`,
            left: `${relativeLeft - (tooltipWidth / 2 - triggerElement.offsetWidth / 2)}px`
          };
          break;
        case 'left':
          this.tooltipStyle = {
            top: `${relativeTop - (tooltipHeight / 2 - triggerElement.offsetHeight / 2)}px`,
            left: `${relativeLeft - tooltipWidth}px`
          };
          break;
        case 'right':
          this.tooltipStyle = {
            top: `${relativeTop - (tooltipHeight / 2 - triggerElement.offsetHeight / 2)}px`,
            left: `${relativeLeft + triggerElement.offsetWidth}px`
          };
          break;
        default:
          break;
      }
    }
  }
});
</script>

<style scoped>
.tooltip-wrapper {
  display: inline-block;
}

.tooltip {
  position: fixed;
  visibility: hidden;
  color: #ffffff;
  background: black;
  width: max-content;
  overflow: visible;
  opacity: 0;
  transition:
    visibility 0.3s linear,
    opacity 0.3s linear;
}

.tooltip-trigger:hover + .tooltip,
.tooltip:hover {
  visibility: visible;
  opacity: 1;
}
</style>
